import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Search, RefreshCcw, Star } from "lucide-react";
import { http } from "@/app/services/api";
import { Input } from "@/app/components/ui/input";
import { Button } from "@/app/components/ui/button";
import { Badge } from "@/app/components/ui/badge";
import { FilterChips } from "@/app/components/ui/filter-chips";
import { Card } from "@/app/components/ui/card";
import { FieldHint } from "@/app/components/ui/field-hint";
import { ROUTES } from "@/app/config/routes";
import { useFormatUiDateTime } from "@/app/utils/format";
import { cn } from "@/app/utils/tw-merge";
import type { AdminAppListItem, AppState } from "@/app/types/api";

const ICON_COLORS = [
  "#d5001f",
  "#0ea5e9",
  "#8b5cf6",
  "#10b981",
  "#f59e0b",
  "#ef4444",
  "#06b6d4",
  "#f97316",
  "#ec4899",
  "#14b8a6",
];
function colorFor(id: string) {
  let h = 0;
  for (const c of id) h = (h * 31 + c.charCodeAt(0)) % ICON_COLORS.length;
  return ICON_COLORS[h];
}

type StateFilter = "" | AppState;

export function AppsPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [search, setSearch] = React.useState("");
  const [state, setState] = React.useState<StateFilter>("");

  const lang = i18n.resolvedLanguage ?? i18n.language ?? "vi";

  const apps = useQuery({
    queryKey: ["admin", "apps", lang],
    queryFn: () =>
      http.get<{ items: AdminAppListItem[]; count: number }>(
        `/api/v1/admin/apps`,
        { limit: 500, locale: lang }
      ),
  });

  const items = apps.data?.items ?? [];
  const filtered = items.filter((a) => {
    if (state && a.state !== state) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (a.name || "").toLowerCase().includes(q) ||
      a.app_id.toLowerCase().includes(q)
    );
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-64">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("apps.search.placeholder")}
            className="pl-8"
          />
        </div>
        <FilterChips<StateFilter>
          value={state}
          onChange={setState}
          options={[
            { value: "", label: t("apps.filter.all") },
            { value: "live", label: t("state.live") },
            { value: "draft", label: t("state.draft") },
            { value: "unavailable", label: t("state.unavailable") },
          ]}
        />
        <Button
          variant="outline"
          size="sm"
          onClick={() => apps.refetch()}
          disabled={apps.isFetching}
        >
          <RefreshCcw className="h-3.5 w-3.5" />
          {t("apps.reload")}
        </Button>
      </div>

      {apps.isLoading ? (
        <FieldHint>{t("apps.loading")}</FieldHint>
      ) : apps.isError ? (
        <p className="text-sm text-destructive">
          {t("apps.error", { error: (apps.error as Error).message })}
        </p>
      ) : filtered.length === 0 ? (
        <Card className="border-dashed bg-muted/30">
          <div className="p-10 text-center text-sm text-muted-foreground">
            {items.length ? (
              t("apps.empty.filtered")
            ) : (
              <span dangerouslySetInnerHTML={{ __html: t("apps.empty.all") }} />
            )}
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {filtered.map((a) => (
            <AppGridCard
              key={a.app_id}
              app={a}
              onClick={() =>
                navigate(ROUTES.appsDetail(a.app_id), {
                  state: {
                    appPreview: {
                      app_id: a.app_id,
                      name: a.name || a.app_id,
                      latest_version: a.latest_version,
                      category: a.category,
                    },
                  },
                })
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}

function AppGridCard({ app, onClick }: { app: AdminAppListItem; onClick: () => void }) {
  const { t } = useTranslation();
  const fmt = useFormatUiDateTime();
  const stateVariant: "success" | "warning" | "default" =
    app.state === "live" ? "success" : app.state === "draft" ? "warning" : "default";
  const stateKey = app.state === "live" ? "state.live" : app.state === "draft" ? "state.draft" : "state.unavailable";
  const letter = (app.name || "?")[0]?.toUpperCase() ?? "?";

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "group flex flex-col gap-2.5 rounded-lg border border-border bg-card p-3.5 text-left transition-all",
        "hover:border-primary hover:shadow-sm"
      )}
    >
      <div className="flex items-center gap-2.5 min-w-0">
        <div
          className="grid h-9 w-9 shrink-0 place-items-center rounded-md text-sm font-semibold text-white"
          style={{ background: colorFor(app.app_id) }}
        >
          {letter}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold truncate">{app.name || app.app_id}</div>
          <div className="text-xs text-muted-foreground truncate">{app.app_id}</div>
          {app.vendor && (
            <div className="mt-0.5 text-xs text-muted-foreground truncate">{app.vendor}</div>
          )}
        </div>
      </div>
      <div className="flex flex-wrap gap-1">
        <Badge variant={stateVariant}>{t(stateKey)}</Badge>
        {app.latest_version && <Badge variant="outline">v{app.latest_version}</Badge>}
        {app.category && <Badge variant="outline">{app.category}</Badge>}
        {app.plan_type === "premium" && (
          <Badge variant="primary">
            <Star className="h-3 w-3 mr-0.5" />
            {t("plan.premium")}
          </Badge>
        )}
        {app.version_count > 1 && (
          <Badge variant="outline">
            {t("apps.versionCount", { count: app.version_count })}
          </Badge>
        )}
      </div>
      {app.updated_at && (
        <div className="text-[11px] text-muted-foreground">
          {t("apps.updatedAt", { date: fmt(app.updated_at) })}
        </div>
      )}
    </button>
  );
}
